import React from "react";
import { Button, Paragraph, Result, Text } from "@/components/base";
import { notifyBusinessError } from "@/lib/error-visibility";

interface ErrorBoundaryState {
	hasError: boolean;
	error?: Error;
	errorId?: string;
	isChunkLoadError?: boolean;
	isDomMutationError?: boolean;
}

interface ErrorBoundaryProps {
	children: React.ReactNode;
	fallback?: React.ComponentType<{ error?: Error; resetError: () => void }>;
	onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

class ErrorBoundaryClass extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
		const errorId = Math.random().toString(36).substring(2, 15);
		return {
			hasError: true,
			error,
			errorId,
			isChunkLoadError: isChunkLoadError(error),
			isDomMutationError: isDomMutationError(error),
		};
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		this.props.onError?.(error, errorInfo);
		notifyBusinessError(error, {
			fallback: "页面渲染失败，请刷新后重试",
			dedupeKey: this.state.errorId || error.message,
		});

		console.group("🚨 ErrorBoundary caught an error");
		console.error("Error:", error.message);
		console.error("Stack:", error.stack);
		console.error("Component Stack:", errorInfo.componentStack);
		console.error("URL:", window.location.href);
		console.groupEnd();
	}

	handleReset = () => {
		this.setState({
			hasError: false,
			error: undefined,
			errorId: undefined,
			isChunkLoadError: undefined,
			isDomMutationError: undefined,
		});
	};

	render() {
		if (this.state.hasError) {
			if (this.props.fallback) {
				const FallbackComponent = this.props.fallback;
				return <FallbackComponent error={this.state.error} resetError={this.handleReset} />;
			}

			return (
				<DefaultErrorFallback
					error={this.state.error}
					resetError={this.handleReset}
					errorId={this.state.errorId}
					isChunkLoadError={this.state.isChunkLoadError}
					isDomMutationError={this.state.isDomMutationError}
				/>
			);
		}

		return this.props.children;
	}
}

const DefaultErrorFallback: React.FC<{
	error?: Error;
	resetError: () => void;
	errorId?: string;
	isChunkLoadError?: boolean;
	isDomMutationError?: boolean;
}> = ({ resetError, errorId, isChunkLoadError, isDomMutationError }) => (
	<div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-8">
		<div className="max-w-2xl w-full">
			<Result
				status="error"
				title={
					isChunkLoadError
						? "页面版本已更新"
						: isDomMutationError
							? "页面内容发生冲突"
							: "应用程序遇到了一个错误"
				}
				subTitle={
					<div>
						<Paragraph>
							{isChunkLoadError
								? "当前页面资源已更新或网络中断，请刷新页面后继续使用。"
								: isDomMutationError
									? "页面节点发生变化，请刷新页面恢复。若开启了网页翻译或修改页面的插件，请先关闭后再试。"
									: "很抱歉，应用程序遇到了一个意外错误。请刷新页面重试；如果持续失败，请联系管理员并提供错误 ID。"}
						</Paragraph>
						{errorId && (
							<Paragraph>
								<Text type="secondary">错误 ID: </Text>
								<Text code>{errorId}</Text>
							</Paragraph>
						)}
					</div>
				}
				extra={[
					<Button
						type="primary"
						key="retry"
						onClick={() => {
							if (isChunkLoadError || isDomMutationError) {
								window.location.reload();
								return;
							}
							resetError();
						}}
					>
						{isChunkLoadError || isDomMutationError ? "刷新页面" : "重新加载"}
					</Button>,
					<Button
						key="home"
						onClick={() => {
							window.location.href = "/";
						}}
					>
						返回首页
					</Button>,
				]}
			/>
		</div>
	</div>
);

function isDomMutationError(error?: Error): boolean {
	return (
		!!error && error.name === "NotFoundError" && /removeChild|insertBefore/.test(error.message)
	);
}

function isChunkLoadError(error?: Error): boolean {
	const message = error?.message ?? "";
	const name = error?.name ?? "";
	return (
		/ChunkLoadError/i.test(name) ||
		/failed to fetch dynamically imported module/i.test(message) ||
		/importing a module script failed/i.test(message) ||
		/loading chunk \d+ failed/i.test(message)
	);
}

export const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children, ...props }) => {
	return <ErrorBoundaryClass {...props}>{children}</ErrorBoundaryClass>;
};

export const withErrorBoundary = <P extends object>(
	Component: React.ComponentType<P>,
	fallback?: React.ComponentType<{ error?: Error; resetError: () => void }>,
) => {
	const WrappedComponent = (props: P) => (
		<ErrorBoundary fallback={fallback}>
			<Component {...props} />
		</ErrorBoundary>
	);

	WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
	return WrappedComponent;
};

export default ErrorBoundary;
